import _ from 'lodash';

import { View } from '@mairistem/planning';

import addPartielPropertyToCreneaux from './addPartielPropertyToCreneaux';
import resizeCreneaux from './resizeCreneaux';
import splitReservationByPeriod from './splitReservationByPeriod';

/**
 * Regroupe toutes les réservations données par créneau, en fonction de la vue.
 *
 * @param {View} view
 * @param {array} reservations
 * @param {array} salles - Liste des salles pour accéder aux horaires
 * @returns
 */
const groupReservationsByCreneau = (view, currentDate, reservations, salles = []) => {
  if (view === View.DAY) {
    // On regroupe les réservations par salles
    const groupedReservationsBySalle = _.groupBy(
      reservations ?? [],
      'identifiantSalles',
    );
    // On parcourt chaque salle et ses réservations
    const creneauxReserves = _.map(
      groupedReservationsBySalle,
      (reservationsList) => {
        // On récupère les réservations triées par date de début
        const orderedReservations = _.orderBy(
          reservationsList,
          'dateDebut',
          'asc',
        );
        // On regroupe les réservations par créneau
        const creneaux = [];

        // Pour chaque réservation
        _.forEach(orderedReservations, (reservation) => {
          const {
            identifiantSalles,
            dateDebut,
            dateFin,
          } = reservation;

          // On détermine l'heure de début/fin du créneau (1/2 heures)
          // si dateDebut dans la première demi-heure, créneau débute à heure pile sinon à 30
          const debutCreneauReservation = dateDebut.get('minute') < 30
            ? dateDebut.startOf('hour')
            : dateDebut.startOf('hour').add(30, 'minute');

          // si dateFin est une heure pile, créneau fini à heure pile,
          // sinon si dateFin dans la première demi-heure inclue, créneau fini à 30
          // sinon fini à la "fin de l'heure"
          const finCreneauReservation = dateFin.get('minute') === 0
            ? dateFin
            : dateFin.get('minute') <= 30 && dateFin.get('minute') > 0
              ? dateFin.startOf('hour').add(30, 'minute')
              : dateFin.endOf('hour');

          // Si on a encore aucun créneau encore, on ajoute sans se soucier
          if (_.isEmpty(creneaux)) {
            creneaux.push({
              identifiant: `creneau@${_.size(creneaux)}`,
              identifiantResource: identifiantSalles,
              dateDebut: debutCreneauReservation,
              dateFin: finCreneauReservation,
              // Toutes les réservations de ce créneau seront dans cette propriété
              reservations: [reservation],
            });
          } else {
          // Sinon on regarde le dernier créneau ajouté
          // On récupère le début et la fin du dernier créneau ajouté
            const lastCreneau = _.last(creneaux);
            const {
              dateFin: finCreneau,
            } = lastCreneau;

            // On ajoute un nouveau créneau si
            // pas la même heure
            if (dateDebut.isAfter(finCreneau, 'hour')
            // même heure mais pas même 1/2h (ou début à 30 et fin à 30)
            || (
              dateDebut.isSame(finCreneau, 'hour')
              && dateDebut.get('minute') >= 30
              && finCreneau.get('minute') <= 30
            )
            // même heure et même minute
            || (dateDebut.isSame(finCreneau))
            ) {
              creneaux.push({
                identifiant: `creneau@${_.size(creneaux)}`,
                identifiantResource: identifiantSalles,
                dateDebut: debutCreneauReservation,
                dateFin: finCreneauReservation,
                // Toutes les réservations de ce créneau seront dans cette propriété
                reservations: [reservation],
              });
            } else { // Dans le cas contraire on est sur le même créneau
            // On ajoute la reservation aux reservations du créneau
              lastCreneau.reservations.push(reservation);

              // Si heure de fin réservation > heure de fin du créneau
              // → On étire la fin du créneau à la fin du créneau
              // de la réservation en cours
              if (dateFin.isAfter(finCreneau)) {
                lastCreneau.dateFin = finCreneauReservation;
              }
            }
          }
        });

        return creneaux;
      },
    );

    const flattenedCreneaux = _.flatten(creneauxReserves);

    const creneaux = resizeCreneaux(view, currentDate, flattenedCreneaux);

    return addPartielPropertyToCreneaux(creneaux, salles, view);
  }

  if (view === View.WEEK) {
    // On regroupe les réservations par salles
    const groupedReservationsBySalle = _.groupBy(
      reservations ?? [],
      'identifiantSalles',
    );

    // On parcourt chaque salle et ses réservations
    const creneauxReserves = _.map(
      groupedReservationsBySalle,
      (reservationsList, identifiantSalles) => {
        // On récupère les réservations triées par date de début
        const orderedReservations = _.orderBy(
          reservationsList,
          'dateDebut',
          'asc',
        );

        // Découper chaque réservation en blocs de demi-journées
        const blocksReservations = [];
        _.forEach(orderedReservations, (reservation) => {
          const blocks = splitReservationByPeriod(reservation, true);
          blocksReservations.push(...blocks);
        });

        // Trier les blocs par date de début
        const orderedBlocks = _.orderBy(
          blocksReservations,
          'dateDebutBlock',
          'asc',
        );

        // On regroupe les blocs par créneau (demi-journée)
        const creneaux = [];

        _.forEach(orderedBlocks, (block) => {
          const {
            dateDebutBlock,
            periodType,
          } = block;

          // Créer une nouvelle réservation avec les dates ORIGINALES
          const reservationForBlock = {
            ..._.omit(
              block,
              ['dateDebutBlock', 'dateFinBlock', 'periodType'],
            ),
          };

          // Déterminer le début et fin du créneau basé sur la période
          const debutCreneau = periodType === 'morning'
            ? dateDebutBlock.clone().startOf('day')
            : dateDebutBlock.clone().startOf('day').hour(12).minute(0);

          const finCreneau = periodType === 'morning'
            ? dateDebutBlock.clone().startOf('day').hour(12).minute(0)
            : dateDebutBlock.clone().endOf('day');

          // Chercher si un créneau existe déjà pour cette demi-journée
          const existingCreneau = _.find(creneaux, (c) => c.dateDebut.isSame(debutCreneau, 'minute')
            && c.dateFin.isSame(finCreneau, 'day')
            && c.identifiantResource === parseInt(identifiantSalles, 10));

          if (existingCreneau) {
            // Ajouter le bloc au créneau existant
            existingCreneau.reservations.push(reservationForBlock);
          } else {
            // Créer un nouveau créneau
            creneaux.push({
              identifiant: `creneau@${_.size(creneaux)}`,
              identifiantResource: parseInt(identifiantSalles, 10),
              dateDebut: debutCreneau,
              dateFin: finCreneau,
              reservations: [reservationForBlock],
            });
          }
        });

        return creneaux;
      },
    );

    const flattenedCreneaux = _.flatten(creneauxReserves);

    const creneaux = resizeCreneaux(view, currentDate, flattenedCreneaux);

    return addPartielPropertyToCreneaux(creneaux, salles, view);
  }

  if (view === View.MONTH) {
    // On regroupe les réservations par salles
    const groupedReservationsBySalle = _.groupBy(
      reservations ?? [],
      'identifiantSalles',
    );

    // On parcourt chaque salle et ses réservations
    const creneauxReserves = _.map(
      groupedReservationsBySalle,
      (reservationsList, identifiantSalles) => {
        // On récupère les réservations triées par date de début
        const orderedReservations = _.orderBy(
          reservationsList,
          'dateDebut',
          'asc',
        );

        // Découper chaque réservation en blocs par journée
        const blocksReservations = [];
        _.forEach(orderedReservations, (reservation) => {
          const blocks = splitReservationByPeriod(reservation, false);
          blocksReservations.push(...blocks);
        });

        // Trier les blocs par date de début
        const orderedBlocks = _.orderBy(
          blocksReservations,
          'dateDebutBlock',
          'asc',
        );

        // On regroupe les blocs par créneau (journée)
        const creneaux = [];

        _.forEach(orderedBlocks, (block) => {
          const { dateDebutBlock } = block;

          // Créer une nouvelle réservation avec les dates ORIGINALES
          const reservationForBlock = {
            ..._.omit(
              block,
              ['dateDebutBlock', 'dateFinBlock'],
            ),
          };

          // Déterminer le début et fin du créneau (journée complète)
          const debutCreneau = dateDebutBlock.clone().startOf('day');
          const finCreneau = dateDebutBlock.clone().endOf('day');

          // Chercher si un créneau existe déjà pour cette journée
          const existingCreneau = _.find(creneaux, (c) => c.dateDebut.isSame(debutCreneau, 'day')
            && c.identifiantResource === parseInt(identifiantSalles, 10));

          if (existingCreneau) {
            // Ajouter le bloc au créneau existant
            existingCreneau.reservations.push(reservationForBlock);
          } else {
            // Créer un nouveau créneau
            creneaux.push({
              identifiant: `creneau@${_.size(creneaux)}`,
              identifiantResource: parseInt(identifiantSalles, 10),
              dateDebut: debutCreneau,
              dateFin: finCreneau,
              reservations: [reservationForBlock],
            });
          }
        });

        return creneaux;
      },
    );

    const flattenedCreneaux = _.flatten(creneauxReserves);

    const creneaux = resizeCreneaux(view, currentDate, flattenedCreneaux);

    return addPartielPropertyToCreneaux(creneaux, salles, view);
  }

  return null;
};

export default groupReservationsByCreneau;
